import type { CategoryItem } from '~/types/root'
import { isSparkitCategorySlug, SPARKIT_ROOT } from '~/utils/categoryRoutes'
import { PORTAL_DBWS_LOGIN } from '~/utils/portalRoutes'

// Single answer to "what does a category-tree node do when it is clicked": the
// header's top-nav buttons and the menu's item list both ask here, so a new
// special case (a marker slug, a new external scheme) is added once. AB#63678

// True when the node has children the customer can see: not hidden (`d-none`) and
// not SparkIt, which is a link to its landing page wherever it appears.
export const hasVisibleChildren = (category: CategoryItem): boolean =>
  !isSparkitCategorySlug(category.slug) &&
  !!category.children?.some(child => child.style !== 'd-none' && !isSparkitCategorySlug(child.slug))

// The "Sign into your personal website" nav item carries the marker slug
// PORTAL_DBWS_LOGIN (not a real page); only this item performs a seamless DBWS
// login. AB#60136
export const isDbwsLoginRoute = (slug: string | undefined): boolean =>
  slug?.toLowerCase() === PORTAL_DBWS_LOGIN

export type CategoryTarget =
  | { kind: 'expand' }
  | { kind: 'dbws' }
  | { kind: 'action', action: () => void }
  | { kind: 'external', url: string }
  | { kind: 'internal', to: string }
  | { kind: 'none' }

// `expands` is the caller's own verdict on children: the menu adds mobile and
// shop-root rules on top of hasVisibleChildren, the header uses it as it is.
export const resolveCategoryTarget = (
  category: CategoryItem,
  expands: boolean,
  buildUrl: (slug: string | undefined) => string
): CategoryTarget => {
  if (expands) { return { kind: 'expand' } }
  if (isDbwsLoginRoute(category.slug)) { return { kind: 'dbws' } }
  const external = !!category.slug?.startsWith('http')
  if (category.action && (!category.slug || external)) { return { kind: 'action', action: category.action } }
  if (!category.slug) { return { kind: 'none' } }
  if (external) { return { kind: 'external', url: category.slug } }
  // Every SparkIt entry goes to the landing page, never to the category. AB#62633
  if (isSparkitCategorySlug(category.slug)) { return { kind: 'internal', to: SPARKIT_ROOT } }
  return { kind: 'internal', to: buildUrl(category.slug) }
}
